import type { ReactNode } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import styles from "./SortEngine.module.css";

export interface SortEngineProps<T> {
  items: T[];
  getId: (item: T) => string;
  renderLabel: (item: T) => ReactNode;
  onChange: (newItems: T[]) => void;
  topLabel: string;
  bottomLabel: string;
  dragHandleLabel: string;
  disabled?: boolean;
}

/**
 * Reusable sort list: the whole card is the drag surface (touch-friendly —
 * no separate handle to hit), plus keyboard support (space to grab, arrows
 * to move, space to drop) via dnd-kit's keyboard sensor on the same card.
 * See spec §6.3.
 */
export function SortEngine<T>({
  items,
  getId,
  renderLabel,
  onChange,
  topLabel,
  bottomLabel,
  dragHandleLabel,
  disabled = false,
}: SortEngineProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = items.map(getId);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    onChange(arrayMove(items, oldIndex, newIndex));
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.endLabel}>{topLabel}</div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <ol className={styles.list}>
            {items.map((item) => (
              <SortCard
                key={getId(item)}
                id={getId(item)}
                disabled={disabled}
                dragHandleLabel={dragHandleLabel}
              >
                {renderLabel(item)}
              </SortCard>
            ))}
          </ol>
        </SortableContext>
      </DndContext>
      <div className={styles.endLabel}>{bottomLabel}</div>
    </div>
  );
}

function arrayMove<T>(array: T[], from: number, to: number): T[] {
  const copy = array.slice();
  const [moved] = copy.splice(from, 1);
  copy.splice(to, 0, moved as T);
  return copy;
}

interface SortCardProps {
  id: string;
  disabled: boolean;
  dragHandleLabel: string;
  children: ReactNode;
}

function SortCard({ id, disabled, dragHandleLabel, children }: SortCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`${styles.card} ${isDragging ? styles.dragging : ""}`}
      aria-label={dragHandleLabel}
      {...attributes}
      {...(disabled ? {} : listeners)}
    >
      <span className={styles.handle} aria-hidden>
        <HandleIcon />
      </span>
      <div className={styles.label}>{children}</div>
    </li>
  );
}

function HandleIcon() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden focusable="false">
      <circle cx="7" cy="5" r="1.6" fill="currentColor" />
      <circle cx="13" cy="5" r="1.6" fill="currentColor" />
      <circle cx="7" cy="10" r="1.6" fill="currentColor" />
      <circle cx="13" cy="10" r="1.6" fill="currentColor" />
      <circle cx="7" cy="15" r="1.6" fill="currentColor" />
      <circle cx="13" cy="15" r="1.6" fill="currentColor" />
    </svg>
  );
}
